import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

// INTERFACES
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';

import { IAppIntegrationShippingPreset } from '../../../app-integration-account/intefaces/app-integration.interface';
import {
  IOrderSalesPicklistPreview,
  IOrderSalesShipstationAddress,
} from '../../../order/interfaces/order-picklist-preview';

// UTILS
import { trackById } from '../../../shared/util/track-by.util';

@Component({
  selector: 'lum-sales-order-sidebar',
  templateUrl: './sales-order-sidebar.component.html',
  styleUrls: ['./sales-order-sidebar.component.scss'],
})
export class SalesOrderSidebarComponent implements OnChanges {
  @Input()
  singleSelectedRow :any;

  @Input()
  consolidatedOrderItems :any[] = [];

  @Input()
  selectedRows :any[] = [];

  @Input()
  warehouseAddress :IOrderSalesShipstationAddress | null = null;

  @Input()
  pickingItems :IOrderSalesPicklistPreview;

  @Input()
  hasShippingLabel :boolean;

  @Input()
  childComponentConfigs :any;

  @Input()
  loadingPicklistItems :boolean = false;

  @Input()
  isSelectAllRows :boolean = false;

  @Input()
  appIntegrationAccountId :number | null = null;

  @Input()
  ftApplyPresets :boolean = false;

  @Output()
  printShippingLabelEvent :EventEmitter<void> = new EventEmitter();

  @Output()
  updateRecipientForm = new EventEmitter<boolean>();

  @Output()
  quickFulfillmentFlowEvent = new EventEmitter<string>();

  @Output()
  adjustmentEntryEvent = new EventEmitter<number>();

  @Output()
  bulkUpdateSuccessEvent = new EventEmitter<boolean>();

  currentPreset :IAppIntegrationShippingPreset | null = null;
  trackById = trackById;
  showAllOrders :boolean = false;
  truncateOrdersThreshold :number = 6;

  constructor(private feature :FeatureToggleService) {}

  ngOnChanges({ selectedRows, singleSelectedRow } :SimpleChanges) :void {
    if (selectedRows) {
      this.currentPreset = null;
    }

    if (
      singleSelectedRow &&
      singleSelectedRow?.previousValue?.salesOrderId !==
        singleSelectedRow.currentValue?.salesOrderId &&
      !singleSelectedRow.firstChange
    ) {
      this.singleSelectedRow = null;
    }
  }

  onUpdateRecipientForm(event :boolean) :void {
    this.updateRecipientForm.emit(event);
  }

  onInitQuickFulfillmentEvent(event :string) :void {
    this.quickFulfillmentFlowEvent.emit(event);
  }

  onCreateAdjustmentEntry(event :number) :void {
    this.adjustmentEntryEvent.emit(event);
  }

  onBulkUpdateSuccess(event :boolean) :void {
    this.bulkUpdateSuccessEvent.emit(event);
  }

  onApplyPreset(preset :IAppIntegrationShippingPreset | null) :void {
    this.currentPreset = preset;
  }

  get hasSameAppIntegrationId() :boolean {
    if (this.selectedRows.length === 0) {
      return false;
    }

    const accountIdsPerItem = this.selectedRows.map((item) =>
      item.channel?.channelIntegrationMapping.map(
        (mapping :any) => mapping.appIntegrationAccountId,
      ),
    );

    let allAreEmpty :boolean = true;

    if (
      this.feature.featureEnabled('utah_FR-577_bulk_update_requested_ship_date')
    ) {
      for (let i = 0, firstIsEmpty; i < accountIdsPerItem.length; i++) {
        const ids = accountIdsPerItem[i];
        const isEmpty = ids == null || ids.length === 0;

        if (!isEmpty) {
          allAreEmpty = false;
        }

        if (i === 0) {
          firstIsEmpty = isEmpty;
        } else if (isEmpty !== firstIsEmpty) {
          return false;
        }
      }

      if (allAreEmpty) {
        return true;
      }
    } else {
      if (accountIdsPerItem.some((ids) => ids == null || ids.length === 0)) {
        return false;
      }
    }

    const commonAccountIds = accountIdsPerItem.reduce((acc, ids) =>
      acc.filter((id :number) => ids.includes(id)),
    );

    return commonAccountIds.length > 0;
  }

  get commonAppIntegrationAccountId() :number | null {
    if (this.selectedRows.length === 0) {
      return null;
    }

    const accountIdsPerItem = this.selectedRows
      .map(
        (item) =>
          item.channel?.pullOrderIntegrationMapping?.appIntegrationAccountId,
      )
      .filter((id) => id != null);

    if (accountIdsPerItem.length === 0) {
      return null;
    }

    const countMap = accountIdsPerItem.reduce((acc, id) => {
      acc[id] = (acc[id] || 0) + 1;

      return acc;
    }, {});

    let mostCommonId = null;
    let maxCount = 0;

    for (const id in countMap) {
      if (countMap[id] > maxCount) {
        mostCommonId = id;
        maxCount = countMap[id];
      }
    }

    return mostCommonId !== null ? Number(mostCommonId) : null;
  }

  get firstSelectedRowAppIntegrationAccountId() :number | null {
    if (this.selectedRows.length === 0) {
      return null;
    }

    return this.selectedRows[0].channel?.pullOrderIntegrationMapping
      ?.appIntegrationAccountId;
  }
}
